/**
 * The blocking strip (wave B2, decision 0057 §1.6): what keeps Lea or the work from moving, in one sentence, with the
 * fixes as buttons (Pair a runner primary, Add a key secondary). `page`: a row on paper with a red square glyph (the
 * Overview, Team and Home); `composer`: the same in a surface box above the chat composer. Not a live region: it is
 * not news, it is a state. Hidden when there is no problem.
 */
import type { WorkspaceRole } from "@minga/protocol"
import { Link, useParams } from "@tanstack/react-router"
import { StatusGlyph } from "@/components/ui/status-dot"
import { useOptionalProject } from "@/features/project/project-context"
import { cn } from "@/lib/utils"
import {
  fixLabel,
  forViewer,
  openShareCardFor,
  type SetupFixContext,
  type SetupProblem,
  type SetupViewer,
  setupFixes,
  setupNote,
  setupSentence,
} from "@/model/setup"
import type { Fix } from "@/model/stops"

const BUTTON =
  "tap inline-flex h-8 shrink-0 items-center justify-center rounded-control px-3.5 text-[14px] whitespace-nowrap max-sm:h-10 max-sm:text-[15px]"
const PRIMARY = "bg-ink font-semibold text-white hover:bg-ink/88"
const SECONDARY = "border border-line-control bg-transparent font-normal text-ink hover:bg-fill"

function FixButton({
  fix,
  role,
  workspaceId,
  primary,
  nameOf,
}: {
  fix: Fix
  role: WorkspaceRole
  workspaceId: string
  primary: boolean
  nameOf?: ((userId: string) => string | null) | undefined
}) {
  const project = useOptionalProject()
  const label = fixLabel(fix, role, nameOf)
  const className = cn(BUTTON, primary ? PRIMARY : SECONDARY)
  switch (fix._tag) {
    case "pair_runner":
      return (
        <Link to="/w/$workspaceId/settings/runners/new" params={{ workspaceId }} className={className}>
          {label}
        </Link>
      )
    case "platform_runner":
      // decision 0166 §2: where the lead runs, on its page's settings (re-walk: at that card); else the Platform
      // Runner's card
      return fix.lead ? (
        <Link
          to="/w/$workspaceId/p/$projectId/agents/$handle"
          params={{ workspaceId, projectId: fix.lead.projectId, handle: fix.lead.handle }}
          search={{ tab: "settings" }}
          hash="where"
          className={className}
        >
          {label}
        </Link>
      ) : (
        <Link to="/w/$workspaceId/settings/runners" params={{ workspaceId }} className={className}>
          {label}
        </Link>
      )
    case "connect_subscription":
      return (
        <Link to="/w/$workspaceId/settings/models" params={{ workspaceId }} className={className}>
          {label}
        </Link>
      )
    case "choose_subscriptions":
      // decision 0166 §3.6: the viewer's choice of subscriptions in this project
      return project ? (
        <Link
          to="/w/$workspaceId/p/$projectId/settings/members"
          params={{ workspaceId, projectId: project.projectId }}
          hash="your-subscriptions"
          className={className}
        >
          {label}
        </Link>
      ) : null
    case "ask_share": {
      // decision 0166 §2: the share card her message opened (0128 addendum); before any message, sending one asks
      const proposalId = fix.proposalId
      const openProposal = project?.openProposal
      if (proposalId && openProposal)
        return (
          <button type="button" className={className} onClick={() => openProposal(proposalId)}>
            {label}
          </button>
        )
      // decision 0168: before any message, the button goes to the composer: the message asks them at once (the strip's
      // sentence says so)
      const toComposer = (e: { preventDefault: () => void }) => {
        const input = document.getElementById("chat-input")
        if (!input) return
        e.preventDefault()
        input.focus()
      }
      return project ? (
        <Link
          data-ask-share=""
          to="/w/$workspaceId/p/$projectId"
          params={{ workspaceId, projectId: project.projectId }}
          search={{ view: "chat" }}
          onClick={toComposer}
          className={className}
        >
          {label}
        </Link>
      ) : null
    }
    case "add_key":
      return role === "member" ? (
        <span className="type-secondary text-ink-2">{label}</span>
      ) : (
        <Link to="/w/$workspaceId/settings/models" params={{ workspaceId }} className={className}>
          {label}
        </Link>
      )
    case "open_runner":
    case "update_runner":
      return (
        <Link
          to="/w/$workspaceId/settings/runners/$runnerId"
          params={{ workspaceId, runnerId: fix.runnerId }}
          search={fix._tag === "open_runner" && fix.profileId ? { profile: fix.profileId } : {}}
          className={className}
        >
          {label}
        </Link>
      )
    default:
      return null
  }
}

/**
 * Decision 0168: before any message, how "Ask Mira to share" works: the next message asks her (the share card opens with
 * it). Null when no login of someone else's would run the lead.
 */
export const askNote = (
  problems: ReadonlyArray<SetupProblem>,
  leadName: string,
  viewer: SetupViewer | undefined,
): string | null => {
  const locked = problems.find((p) => p._tag === "no_cli" && p.lockedTo !== undefined)
  if (locked?._tag !== "no_cli" || locked.lockedTo === undefined || locked.notChosen === true) return null
  const who = viewer?.nameOf?.(locked.lockedTo) ?? locked.owner ?? "its owner"
  return `Your next message to ${leadName} asks ${who} to share it.`
}

export function SetupStrip({
  problems,
  leadName,
  waiting,
  variant,
  workspaceRole: role = "owner",
  workspaceId: workspaceProp,
  className,
  skipFixes,
  viewer,
  fixContext,
}: {
  problems: ReadonlyArray<SetupProblem>
  leadName: string
  waiting: number
  variant: "page" | "composer"
  /** Members can't add a model key: they read "Ask an owner to add a key". */
  workspaceRole?: WorkspaceRole | undefined
  /** The workspace the fixes link into; defaults to the route's `$workspaceId`. */
  workspaceId?: string | undefined
  className?: string | undefined
  /** Fixes the page already offers elsewhere (Home's checklist): each fix shows once per screen (UX pass phase 2). */
  skipFixes?: ReadonlyArray<Fix["_tag"]> | undefined
  /** Polish 2026-10-23 §5: who reads it: an offline runner names its owner, and the fixes and the note follow. */
  viewer?: SetupViewer | undefined
  /** Decision 0166 §2: the lead (Use the Platform Runner opens where it runs) and the open share cards. */
  fixContext?: SetupFixContext | undefined
}) {
  const params = useParams({ strict: false }) as { readonly workspaceId?: string }
  const workspaceId = workspaceProp ?? params.workspaceId
  const project = useOptionalProject()
  if (problems.length === 0) return null
  const seen = forViewer(problems, viewer)
  const note = setupNote(seen, leadName, role)
  // decision 0166 §2: on a project page, the lead's settings and the viewer's open share cards
  const lead = project?.model.agents.find((a) => a.id === project.model.leadAgentId)
  const fixCtx: SetupFixContext | undefined =
    fixContext ??
    (project && lead
      ? {
          lead: { projectId: project.projectId, handle: lead.handle },
          shareCardFor: (ownerId) => openShareCardFor(project.state, ownerId, project.userId),
        }
      : undefined)
  const fixes = setupFixes(seen, role, fixCtx).filter((f) => !skipFixes?.includes(f._tag))
  // decision 0168: "Ask Mira to share" before any share card is open: the next message asks her
  const asks = fixes.some((f) => f._tag === "ask_share" && f.proposalId === undefined)
  const sentence = [
    setupSentence(seen, leadName, waiting),
    note,
    asks ? askNote(seen, leadName, viewer) : null,
  ]
    .filter((x): x is string => Boolean(x))
    .join(" ")
  return (
    <section
      aria-label="Setup"
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-2",
        variant === "page"
          ? "bg-paper py-3"
          : "rounded-sheet border border-rule bg-surface px-3.5 py-3 max-sm:px-3 max-sm:py-2",
        className,
      )}
    >
      {variant === "page" && <StatusGlyph state="blocked" label={false} />}
      {/* On the phone the sentence takes its own row and the buttons wrap under it (never a 170px column). Re-walk
          2026-10-28: on wider screens too, it keeps at least 18rem and the fixes wrap under it (it collapsed to one
          word per line next to a long "Send your message: …" line). */}
      <p
        className={cn(
          "min-w-0 flex-1 basis-[18rem] text-[14px] text-ink",
          variant === "page" ? "max-sm:basis-[calc(100%-1.5rem)]" : "max-sm:basis-full",
        )}
      >
        {sentence}
      </p>
      {workspaceId && fixes.length > 0 && (
        <div className="flex min-w-0 flex-wrap gap-1.5">
          {fixes.map((fix, i) => (
            <FixButton
              key={fix._tag}
              fix={fix}
              role={role}
              workspaceId={workspaceId}
              primary={i === 0}
              nameOf={viewer?.nameOf}
            />
          ))}
        </div>
      )}
    </section>
  )
}
